// zr-core — framework-agnostic domain model, signaling protocol, state machine, events.
// No DOM, no React, no Node-only APIs. Portable across web and React Native.

import type { CallPhase, CallEndReason, IceConnectionState } from './callState';
import type { DeviceContext } from './deviceContext';
import type { PeerHeartbeat } from './peerState';
import type { DirectoryPeer } from './presenceDirectory';

export type CallMode = 'call' | 'group' | 'broadcast';
export type RoomState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'ended';
export type TrackKind = 'audio' | 'video' | 'screen';

// ---------------------------------------------------------------------------
// Signaling Infrastructure Provider — the developer-selectable media+signaling
// plane. This is the top-level architectural switch for the SDK:
//
//   • 'serverless-matchmaker' — pure RAW WebRTC P2P full-mesh, discovered via the
//     ZeroRate CoTurn ICE engine and brokered by serverless polling signaling.
//     Lowest latency / near-zero server cost. HARD-CAPPED at 10 participants
//     because mesh egress scales O(N²).
//
//   • 'whip-whep' — stream ingress & egress through the self-hosted ZeroRate
//     WebRTC Media Kit (forked LiveKit: SFU + WHIP ingest + Egress). The path for
//     sessions larger than the mesh can sustain. Surfaced to end users simply as
//     "stream ingress & egress".
//
// `auto` is not a stored value: when a developer leaves the provider unset, the
// SDK resolves it from room size/mode (see resolveProvider in zr-webrtc) so the
// existing zero-config behaviour is preserved.
// ---------------------------------------------------------------------------
export type SignalingProvider = 'serverless-matchmaker' | 'whip-whep';

/** Hard ceiling on a single raw-P2P mesh session. Beyond this, use 'whip-whep'. */
export const MAX_MATCHMAKER_PARTICIPANTS = 10 as const;

/** Hard ceiling on a single SFU session (stream ingress & egress). */
export const MAX_SFU_PARTICIPANTS = 200 as const;

/** Human-facing labels — keep the engine, docs, and UI agreeing on one name. */
export const PROVIDER_LABELS: Record<SignalingProvider, { name: string; userFacing: string }> = {
  'serverless-matchmaker': { name: 'Serverless MatchMaker (P2P + Polling)', userFacing: 'Serverless MatchMaker (P2P + Polling)' },
  'whip-whep': { name: 'Localised WebRTC Media Infra + SFU + Stream Ingress + Egress', userFacing: 'Localised WebRTC Media Infra + SFU + Stream Ingress + Egress' },
};

export interface TrackInfo {
  id: string;
  kind: TrackKind;
  muted: boolean;
}

export interface Participant {
  id: string;
  displayName?: string;
  isLocal: boolean;
  isHost: boolean;
  tracks: TrackInfo[];
}

export interface Room {
  id: string;
  mode: CallMode;
  maxParticipants: number;
  state: RoomState;
  participants: Participant[];
  accepted?: boolean;
}

// ---------------------------------------------------------------------------
// Signaling protocol (versioned wire schema shared by web + mobile)
// ---------------------------------------------------------------------------
export const SIGNAL_PROTOCOL_VERSION = 1 as const;

export type SignalMessage =
  | { type: 'auth'; token: string }
  | { type: 'auth-ok'; peerId: string }
  | { type: 'auth-error'; error: string }
  | { type: 'join'; roomId: string; from?: string }
  | { type: 'participants'; roomId: string; participants: string[] }
  | { type: 'leave'; roomId: string; to?: string; from?: string }
  | { type: 'invite'; roomId: string; from: string; to: string; displayName?: string; password?: string; silent?: boolean }
  | { type: 'accept'; roomId: string; from: string; to?: string }
  | { type: 'decline'; roomId: string; to: string; from?: string }
  | { type: 'cancel'; roomId: string; to: string; from?: string }
  | { type: 'hangup'; roomId: string; to?: string; from?: string }
  | { type: 'offer'; roomId: string; to: string; sdp: string; from?: string }
  | { type: 'answer'; roomId: string; to: string; sdp: string; from?: string }
  | { type: 'candidate'; roomId: string; to: string; candidate: string; from?: string }
  | { type: 'broadcast-control'; roomId: string; action: 'go-live' | 'stop'; targets?: string[] }
  // In-call interaction control plane (rides the same signaling mailbox in both P2P and SFU).
  | { type: 'reaction'; roomId: string; from: string; emoji: string; to?: string }
  | { type: 'raise-hand'; roomId: string; from: string; raised: boolean; to?: string }
  // Per-participant public-key identity announcement (P2P identity + MITM-resistant verification).
  | { type: 'identity'; roomId: string; from: string; to?: string; publicKey: string };

export interface SignalingClient {
  connect(token: string): Promise<void>;
  send(msg: SignalMessage): void;
  /** Returns an unsubscribe function. */
  on(handler: (msg: SignalMessage) => void | Promise<void>): () => void;
  close(): void;
  getParticipants?(roomId: string): Promise<string[]>;
  joinRoom?(roomId: string): Promise<void> | void;
  leaveRoom?(roomId: string): Promise<void> | void;
}

/**
 * OPTIONAL WebSocket signaling transport. Not used by the default flows — the Serverless
 * MatchMaker (P2P) path uses {@link PollSignalingClient}, and the Localised WebRTC Media
 * Infra (WHIP-WHEP/SFU) path uses HTTP WHIP/WHEP. It is bundled for apps that self-host a
 * persistent WebSocket signaling hub (see `server/signaling`); pass an instance as
 * `createClient({ signaling })` to opt in.
 */
export class WebSocketSignalingClient implements SignalingClient {
  private ws?: WebSocket;
  private handlers = new Set<(msg: SignalMessage) => void | Promise<void>>();
  private authenticated = false;
  private currentRoomId?: string;
  private currentParticipants = new Set<string>();

  constructor(private url: string) {}

  connect(token: string): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.authenticated) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);
        let settled = false;
        const fail = (err: unknown) => {
          if (settled) return;
          settled = true;
          reject(err instanceof Error ? err : new Error(String(err)));
        };

        this.ws.onopen = () => {
          this.ws?.send(JSON.stringify({ type: 'auth', token } satisfies SignalMessage));
        };

        this.ws.onerror = (err) => {
          fail(err);
        };

        this.ws.onclose = () => {
          this.authenticated = false;
          this.currentRoomId = undefined;
          this.currentParticipants.clear();
          fail(new Error('WebSocket signaling connection closed before authentication completed.'));
        };

        this.ws.onmessage = async (event) => {
          try {
            const msg = JSON.parse(event.data) as SignalMessage;
            if (msg.type === 'auth-ok') {
              this.authenticated = true;
              if (!settled) {
                settled = true;
                resolve();
              }
              return;
            }
            if (msg.type === 'auth-error') {
              fail(new Error(msg.error || 'WebSocket signaling authentication failed.'));
              this.close();
              return;
            }
            if (msg.type === 'participants' && msg.roomId === this.currentRoomId) {
              this.currentParticipants = new Set(msg.participants);
            } else if (msg.type === 'join' && msg.roomId === this.currentRoomId && msg.from) {
              this.currentParticipants.add(msg.from);
            } else if (msg.type === 'leave' && msg.roomId === this.currentRoomId && msg.from) {
              this.currentParticipants.delete(msg.from);
            }
            for (const handler of this.handlers) {
              await Promise.resolve(handler(msg));
            }
          } catch (e) {
            // Suppress parse warnings on non-JSON heartbeats
          }
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  send(msg: SignalMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on(handler: (msg: SignalMessage) => void | Promise<void>): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  joinRoom(roomId: string): void {
    this.currentRoomId = roomId;
    this.currentParticipants.clear();
    this.send({ type: 'join', roomId });
  }

  leaveRoom(roomId: string): void {
    if (this.currentRoomId === roomId) {
      this.currentRoomId = undefined;
      this.currentParticipants.clear();
    }
    this.send({ type: 'leave', roomId });
  }

  async getParticipants(roomId: string): Promise<string[]> {
    if (roomId !== this.currentRoomId) return [];
    return Array.from(this.currentParticipants);
  }

  close(): void {
    if (this.ws) {
      this.authenticated = false;
      this.currentRoomId = undefined;
      this.currentParticipants.clear();
      this.ws.close();
      this.ws = undefined;
    }
  }
}


// ---------------------------------------------------------------------------
// Connection health — passive network quality derived from WebRTC getStats.
// Computed transparently alongside the live call; never injects probe traffic.
// ---------------------------------------------------------------------------
export type QualityLevel = 'excellent' | 'good' | 'fair' | 'poor';

export interface QualityDetails {
  rtt: number;                   // round-trip time, ms
  lossRate: number;              // packet loss, %
  jitter: number;                // ms
  downKbps: number;              // inbound throughput estimate (live speed test)
  upKbps: number;                // outbound throughput estimate (live speed test)
  availableOutgoingKbps: number; // bandwidth estimate (BWE) from the candidate pair
}

export interface ActiveSpeaker {
  participantId: string;
  level: number;
  isLocal: boolean;
}

// ---------------------------------------------------------------------------
// ICE server shaping — bias the media path toward UDP.
// ---------------------------------------------------------------------------

/** A structural ICE server (DOM-free) so zr-core can reason about ICE without the DOM type. */
export interface IceServerLike {
  urls: string | string[];
  username?: string;
  credential?: string;
}

function iceUrlRank(url: string): number {
  const u = String(url).toLowerCase();
  if (u.startsWith('stun:')) return 0; // direct-path srflx first
  if (u.startsWith('turns:')) return 3; // TLS relay — slowest, last
  if (u.startsWith('turn:')) return u.includes('transport=tcp') ? 2 : 1; // TURN/UDP before TURN/TCP
  return 2;
}

/**
 * Reorder ICE servers + their URLs to PREFER A UDP MEDIA PATH: STUN (direct srflx) → TURN/UDP →
 * TURN/TCP → TURNS/TLS (relay) last. The browser still decides final candidate priority
 * (host > srflx > relay), but gathering the UDP relay before the TLS relay biases toward a UDP
 * media path and stops the slow TLS relay from being nominated first on restrictive/African
 * networks — the exact failure where a call needlessly ran over TURN/TCP. Pure + testable.
 */
export function orderIceServersUdpFirst<T extends IceServerLike>(servers: T[]): T[] {
  const withSorted = servers.map((s) => ({
    ...s,
    urls: (Array.isArray(s.urls) ? [...s.urls] : [s.urls]).sort((a, b) => iceUrlRank(a) - iceUrlRank(b)),
  }));
  return withSorted.sort((a, b) => iceUrlRank(a.urls[0]) - iceUrlRank(b.urls[0]));
}

/**
 * Derive a `stun:` URL from the TURN host so direct-path srflx candidates are ALWAYS gathered
 * (a `turn:` server also answers STUN, but an explicit STUN entry makes the direct-UDP path more
 * reliable) — enabling direct P2P instead of forcing a relay. Returns undefined if none parse.
 */
export function stunFromTurnUris(uris: string[]): string | undefined {
  for (const uri of uris) {
    const m = String(uri).match(/^turns?:([^?]+)/i);
    if (m && m[1]) return `stun:${m[1]}`;
  }
  return undefined;
}

/**
 * Maps live network metrics to a 1-4 score + label — the level is the WORST of loss, jitter
 * and RTT.
 *
 * PERCEIVED call quality is driven mostly by packet LOSS and JITTER (they cause the audio/video
 * glitches you actually notice); RTT only adds conversational delay, so a low-loss path is a
 * perfectly usable call even at a high RTT. Older versions were RTT-primary, which mislabeled a
 * healthy call as "poor" whenever the media went over a **TURN relay** — and relayed paths (an
 * extra intercontinental hop) ALWAYS carry a higher RTT baseline. So we (a) make loss/jitter the
 * primary signals and (b) relax the RTT ceilings when `relayed`, so a 0%-loss relayed call reads
 * as "good/fair", not "poor / weak local network". Thresholds stay lenient for African mobile
 * (Nigeria MTN/Glo/Airtel/9mobile, shared community links).
 */
export function classifyQuality(metrics: {
  rtt: number;
  lossRate: number;
  jitter?: number;
  /** True when the active media path is a TURN relay — RTT baseline is inherently higher. */
  relayed?: boolean;
}): { score: number; level: QualityLevel } {
  const { rtt, lossRate, jitter = 0, relayed = false } = metrics;
  const rttPoor = relayed ? 750 : 500;
  const rttFair = relayed ? 500 : 320;
  const rttGood = relayed ? 320 : 180;
  const byLoss = lossRate > 8 ? 1 : lossRate > 3 ? 2 : lossRate > 1 ? 3 : 4;
  const byJitter = jitter > 60 ? 1 : jitter > 35 ? 2 : jitter > 20 ? 3 : 4;
  const byRtt = rtt > rttPoor ? 1 : rtt > rttFair ? 2 : rtt > rttGood ? 3 : 4;
  const score = Math.min(byLoss, byJitter, byRtt);
  const level: QualityLevel = score <= 1 ? 'poor' : score === 2 ? 'fair' : score === 3 ? 'good' : 'excellent';
  return { score, level };
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
export type RoomEvent =
  | { type: 'participant-joined'; participant: Participant }
  | { type: 'participant-left'; participantId: string }
  | { type: 'track-subscribed'; participantId: string; track: TrackInfo }
  | { type: 'active-speakers-changed'; speakers: ActiveSpeaker[] }
  | { type: 'state-changed'; state: RoomState }
  | { type: 'quality-changed'; participantId: string; score: number; level: QualityLevel; details: QualityDetails }
  // RAW link capacity (active fast.com-style probe) — distinct from the media throughput in quality-changed.
  | { type: 'link-capacity'; downMbps: number; upMbps: number; latencyMs?: number; at: number; endpoint?: string }
  // Online Presence Directory changed — the live roster of peers + online/e2ee/turns/role/availability.
  | { type: 'presence-changed'; peers: DirectoryPeer[] }
  | { type: 'screen-share-changed'; active: boolean }
  | { type: 'provider-selected'; provider: SignalingProvider; transport: 'p2p' | 'sfu'; explicit: boolean }
  // Call lifecycle (WhatsApp flow) — the per-peer ICE state and the folded call phase.
  | { type: 'peer-connection-state-changed'; participantId: string; state: IceConnectionState }
  | { type: 'incoming-call'; roomId: string; from: string; displayName?: string; silent?: boolean }
  | { type: 'call-phase-changed'; phase: CallPhase; reason?: CallEndReason }
  // In-call interactions (reactions + raised-hand roster) — uniform across P2P and SFU.
  | { type: 'reaction-received'; from: string; emoji: string; at: number }
  | { type: 'hand-raised-changed'; participantId: string; raised: boolean; raisedHands: { peerId: string; at: number }[] }
  // A peer announced its public-key identity: verified fingerprint + safety number for it.
  | { type: 'peer-identity'; participantId: string; publicKey: string; fingerprint: string; safetyNumber: string }
  // Device/browser/native context enriched with optional server-side network intelligence.
  | { type: 'device-context'; context: DeviceContext }
  // Real-time per-peer keep-alive heartbeat: connection state + live transport + quality.
  | { type: 'peer-heartbeat'; participantId: string; heartbeat: PeerHeartbeat }
  | { type: 'turn-liveness-changed'; alive: boolean; turnReachable: boolean; credentialsValid: boolean; expiresAt?: string }
  // Proactive rotation: credentials are nearing expiry; the SDK is pre-emptively re-minting.
  | { type: 'turn-credentials-expiring'; expiresAt: string; inMs: number }
  | { type: 'turn-credentials-refreshed' }
  // A peer has stayed disconnected across repeated ICE restarts — escalate to a full recovery
  // (re-mint TURN credentials + fresh relay allocation), since a restart can't fix a dead relay.
  | { type: 'peer-reconnect-escalate'; participantId: string; attempts: number };

export type EventHandler = (e: RoomEvent) => void;

export class EventBus {
  private handlers = new Set<EventHandler>();
  on(h: EventHandler): () => void {
    this.handlers.add(h);
    return () => {
      this.handlers.delete(h);
    };
  }
  emit(e: RoomEvent): void {
    // Isolate handlers: one throwing listener must not block delivery to the rest.
    for (const h of this.handlers) {
      try {
        h(e);
      } catch (err) {
        console.error('EventBus: handler threw, continuing delivery:', err);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Room state machine
// ---------------------------------------------------------------------------
const TRANSITIONS: Record<RoomState, RoomState[]> = {
  idle: ['connecting'],
  connecting: ['connected', 'ended'],
  connected: ['reconnecting', 'ended'],
  reconnecting: ['connected', 'ended'],
  ended: [],
};

export function canTransition(from: RoomState, to: RoomState): boolean {
  return TRANSITIONS[from].includes(to);
}

export function transition(room: Room, to: RoomState): Room {
  if (!canTransition(room.state, to)) {
    throw new Error(`Illegal room state transition: ${room.state} -> ${to}`);
  }
  return { ...room, state: to };
}

export * from './pollSignaling';
export * from './callState';
export * from './peerState';
export * from './interactions';
export * from './scheduling';
export * from './e2ee';
export * from './videoEffects';
export * from './identity';
export * from './deviceContext';
export * from './callUx';
export * from './linkCapacity';
export * from './product';
export * from './presenceDirectory';
export * from './callQueue';

// ---------------------------------------------------------------------------
// Identity helpers
// ---------------------------------------------------------------------------
/** Peer ids that must never be relied on as unique — equal ids deadlock negotiation. */
export const RESERVED_PEER_IDS = new Set(['', 'local-peer', 'test-user', 'me', 'peer', 'user']);

/**
 * Generate a collision-resistant peer id. Uses the platform crypto UUID when available
 * (browser + Node), falling back to a timestamp+random token. Use this to guarantee a
 * unique `selfId` so two participants can never share an id (which would break the
 * polite/impolite role resolution in perfect-negotiation).
 */
export function generatePeerId(prefix = 'peer'): string {
  const g: any = globalThis as any;
  if (g.crypto?.randomUUID) return `${prefix}-${g.crypto.randomUUID()}`;
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now().toString(36)}-${rand}`;
}

/** True when an id is safe to use as a unique participant identity. */
export function isUsablePeerId(id: string | undefined | null): id is string {
  return typeof id === 'string' && id.length > 0 && !RESERVED_PEER_IDS.has(id);
}
